import {
  Query,
  VaroAdvanceProductsResultTypenames,
} from '@varo/graphql-schema-types';
import { CALCULATED_MINIMUM_POLLING_TIME } from 'features/advance/constants/constants';
import useAdvanceProductsV2 from 'features/advance/hooks/useAdvanceProductsV2';
import { useEffect, useState } from 'react';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { CombinedError } from 'urql';

enum LightningEligibilityStates {
  Initial = 'INITIAL',
  StartFetchingVaroAdvanceProducts = 'START_FETCHING_VARO_ADVANCE_PRODUCTS',
  FetchingVaroAdvanceProducts = 'FETCHING_VARO_ADVANCE_PRODUCTS',
  FetchingInPendingStatus = 'FETCHING_IN_PENDING_STATUS',
  FinalStatus = 'FINAL_STATUS',
  FetchingHomeTiles = 'FETCHING_HOME_TILES',
  HomeTilesComplete = 'HOME_TILES_COMPLETE',
}

interface GetCurrentStateParams {
  shouldPoll: boolean;
  pollingStarted: boolean;
  fetchingAdvanceProducts: boolean;
  status: VaroAdvanceProductsResultTypenames | undefined;
  fetchingHomeTiles: boolean;
  counter: number | null;
  homeTilesData: Query | undefined;
  homeTilesError: CombinedError | undefined;
}

const getCurrentState = ({
  shouldPoll,
  pollingStarted,
  fetchingAdvanceProducts,
  status,
  fetchingHomeTiles,
  counter,
  homeTilesData,
  homeTilesError,
}: GetCurrentStateParams) => {
  if (homeTilesData || homeTilesError) {
    return LightningEligibilityStates.HomeTilesComplete;
  }

  if (
    !fetchingAdvanceProducts &&
    !status &&
    !fetchingHomeTiles &&
    !homeTilesData &&
    !homeTilesError &&
    !shouldPoll
  ) {
    return LightningEligibilityStates.Initial;
  }

  switch (status) {
    case VaroAdvanceProductsResultTypenames.VaroAdvanceProductsQualified:
    case VaroAdvanceProductsResultTypenames.VaroAdvanceProductsNotQualified:
      if (counter && counter > CALCULATED_MINIMUM_POLLING_TIME) {
        return LightningEligibilityStates.FetchingVaroAdvanceProducts;
      }
      return LightningEligibilityStates.FinalStatus;
    case VaroAdvanceProductsResultTypenames.VaroAdvanceProductsPendingEligibility:
      if (counter !== null && counter > 0) {
        return LightningEligibilityStates.FetchingInPendingStatus;
      }
      return LightningEligibilityStates.FinalStatus;
    default:
      break;
  }

  if (shouldPoll && !pollingStarted) {
    return LightningEligibilityStates.StartFetchingVaroAdvanceProducts;
  }

  if (fetchingAdvanceProducts) {
    return LightningEligibilityStates.FetchingVaroAdvanceProducts;
  }

  if (fetchingHomeTiles) {
    return LightningEligibilityStates.FetchingHomeTiles;
  }
};

const useGetLightningEligibility = ({
  shouldPoll,
}: {
  shouldPoll: boolean;
}) => {
  const [pollingStarted, setPollingStarted] = useState(false);

  const {
    counter,
    data: eligibilityData,
    eligibilityStatus,
    fetching: fetchingAdvanceProducts,
    error: advanceProductsNetworkError,
  } = useAdvanceProductsV2({
    pauseQuery: true,
    shouldPoll: pollingStarted,
    pollInterval: 3000,
  });

  const {
    refreshTiles,
    fetching: fetchingHomeTiles,
    data: homeTilesData,
    error: homeTilesError,
  } = useRefreshHomeTiles([
    HomeTileTypeIds.LINKED_ACCOUNTS_TILE,
    HomeTileTypeIds.EXTERNAL_ACCOUNTS_TILE,
    HomeTileTypeIds.LIST_ITEM_MANAGE_EXTERNAL_ACCOUNTS,
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_FOR_YOU,
    HomeTileTypeIds.ADVANCE_ACCOUNT_CARD_SQUARE_TILE,
    HomeTileTypeIds.VXL_SIDE_BY_SIDE_TILE_ACCOUNT_CARD,
  ]);

  const currentState = getCurrentState({
    shouldPoll,
    pollingStarted,
    fetchingAdvanceProducts,
    status: eligibilityStatus,
    counter,
    fetchingHomeTiles,
    homeTilesData,
    homeTilesError,
  });

  useEffect(() => {
    if (
      currentState ===
      LightningEligibilityStates.StartFetchingVaroAdvanceProducts
    ) {
      setPollingStarted(true);
    }
  }, [currentState]);

  const { advanceProductsError } = eligibilityData;

  useEffect(() => {
    if (
      currentState === LightningEligibilityStates.FinalStatus &&
      pollingStarted
    ) {
      refreshTiles();
      setPollingStarted(false);
    }
  }, [currentState, pollingStarted, refreshTiles]);

  // if homeTiles refresh errors, we don't want to show the error screen
  // in the following screens we will attempt to refresh the home tiles again
  const hasError = Boolean(advanceProductsNetworkError || advanceProductsError);

  const homeTilesQueryComplete =
    currentState === LightningEligibilityStates.HomeTilesComplete;

  return {
    fetching: fetchingAdvanceProducts || fetchingHomeTiles || pollingStarted,
    hasError,
    counter,
    eligibilityStatus: homeTilesQueryComplete ? eligibilityStatus : null,
    data: eligibilityData,
    isFinalEligibilityStatus: homeTilesQueryComplete,
  };
};

export default useGetLightningEligibility;
